import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { isEqual, cloneDeep } from 'lodash'
import { useTranslation } from 'react-i18next'

import Icon from '../../../../components/Icons'
import ModalContainer from '../../../../components/ModalContainer/ModelContainer'
import { fetchSingleBuilding } from '../../../../reducers/buildingSelectorReducer'
import { selectUserRole } from '../../../../reducers/userDataReducer'
import {
  transformApiTaskToTaskState,
  INITIAL_TASK_STATE,
  formatResidentStatusesForFilter,
  validateForm,
} from '../helpers'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import api from '../../../../api/api'
import {
  getAmenities,
  selectAmenities,
} from '../../../../reducers/Organisation/reservationsReducer'

import TaskSettings from './steps/TaskSettings'
import TaskData from './steps/TaskData'

const TABS = {
  TASK_DATA: 'taskData',
  TASK_SETTINGS: 'taskSettings',
}
const ALREADY_EXISTS_ERROR = 'Task with the same name already exists'

const ChecklistsUpdateCustomTaskModal = ({
  selectedBuilding,
  taskId,
  onClose = () => {},
  fetchSingleBuilding = () => {},
  getAmenities,
  amenities,
  userRole,
}) => {
  const { t } = useTranslation(['checklists-create-task'])
  const isAdmin = userRole === 'Administrator'
  const [currentTab, setCurrentTab] = useState(TABS.TASK_DATA)
  const [isIntegrationTask, setIsIntegrationTask] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [currentTask, setCurrentTask] = useState(null)
  const [formState, setFormState] = useState(INITIAL_TASK_STATE)
  const [availableStatuses, setAvailableStatuses] = useState([])
  const [nameError, setNameError] = useState('')
  const [noInputsError, setNoInputsError] = useState('')
  const { displaySnackbar } = useSnackbar()

  const [yardiCustomRecords, setYardiCustomRecords] = useState(null)

  const handleFieldChange = ({ target: { name, value } }) => {
    if (name === 'serviceTask' && value === true) {
      setFormState((prev) => ({
        ...prev,
        serviceTask: true,
        dueDateTriggerVisibility: false,
        approvalRequired: false,
        signatureRequired: false,
      }))
      return
    }
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const fetchCustomTask = async () => {
    setIsLoading(true)
    try {
      const { data } = await api.fetchCustomTaskById(taskId)
      const task = transformApiTaskToTaskState(data)
      const isIntegration = !!data.integrationProvider
      setCurrentTab(isIntegration ? TABS.TASK_SETTINGS : TABS.TASK_DATA)
      setIsIntegrationTask(isIntegration)
      setFormState(task)
      setCurrentTask(task)
    } catch ({ message }) {
      displaySnackbar(message, true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (taskId) {
      fetchCustomTask()
    }
  }, [taskId])

  const onUpdate = async (updatedTask) => {
    if (!isEqual(currentTask, updatedTask)) {
      setIsLoading(true)
      try {
        const {
          name,
          attachment,
          instructions,
          dueDateDays,
          dueDateTrigger,
          dueDateCondition,
          dueDateTriggerVisibility,
          assignTaskTrigger,
          assignTaskDays,
          assignTaskRelative,
          assignTriggerVisibility,
          approvalRequired,
          signatureRequired,
          limitTaskAssignmentVisibility,
          assignTaskStatusTrigger,
          amenityId,
          inputs,
          serviceTask,
        } = cloneDeep(updatedTask)

        const updatedInputs = inputs.map((input) => {
          if (input.providersInfo && input.providersInfo.length) {
            input.providersInfo = input.providersInfo.map((provider) => provider.id)
          }
          if (
            input.inputType === 'realpage_pet' ||
            input.inputType === 'realpage_vehicle' ||
            input.inputType === 'vehicle_form'
          ) {
            input.isRequired = true
          }
          input.yardiOtherOccupantCustomRecord = input.yardiOtherOccupantCustomRecord
            ? input.yardiOtherOccupantCustomRecord.id
            : null
          input.yardiResidentCustomRecord = input.yardiResidentCustomRecord
            ? input.yardiResidentCustomRecord.id
            : null
          delete input._id
          if (input.form && input.form.length) {
            input.form = input.form.map(({ _id: _unused, ...rest }) => rest)
          }
          return input
        })

        const requestData = {
          buildingId: selectedBuilding ? selectedBuilding.id : undefined,
          name,
          attachment,
          instructions,
          ...(dueDateTriggerVisibility
            ? { dueDateDays, dueDateTrigger, dueDateCondition }
            : { dueDateDays: null, dueDateTrigger: null, dueDateCondition: null }),
          ...(assignTriggerVisibility
            ? { assignTaskTrigger, assignTaskDays, assignTaskRelative }
            : { assignTaskTrigger: null, assignTaskDays: null, assignTaskRelative: null }),
          assignTaskStatusTrigger: limitTaskAssignmentVisibility ? assignTaskStatusTrigger : null,
          approvalRequired,
          signatureRequired,
          assignTriggerVisibility,
          dueDateTriggerVisibility,
          inputs: updatedInputs,
          amenityId,
          serviceTask,
        }
        await api.updateCustomTask(taskId, requestData)
        displaySnackbar(t('checklists-create-task:updateCustomTaskModal.saveSuccess'))
        await fetchSingleBuilding(selectedBuilding.id)
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
        if (message) {
          displaySnackbar(message, true)
          if (message === ALREADY_EXISTS_ERROR) {
            setNameError(ALREADY_EXISTS_ERROR)
            setCurrentTab(isIntegrationTask ? TABS.TASK_SETTINGS : TABS.TASK_DATA)
          }
        } else {
          displaySnackbar(error.message, true)
        }
      } finally {
        onClose()
      }
    } else {
      onClose()
    }
  }

  const fetchAvailableStatuses = async () => {
    try {
      const { data } = await api.fetchResidentStatuses()
      setAvailableStatuses(formatResidentStatusesForFilter(data))
    } catch ({ message }) {
      displaySnackbar(message, true)
    }
  }

  const isValidForm = () =>
    validateForm({
      formState,
      isIntegrationTask,
      tab: TABS.TASK_DATA,
      setFormState,
      setNameError,
      setNoInputsError,
      setCurrentTab,
      t,
    })

  const onSave = () => {
    if (isValidForm()) onUpdate(formState)
  }

  const onChangeTab = (tab) => {
    if (isValidForm()) setCurrentTab(tab)
  }

  useEffect(() => {
    fetchAvailableStatuses()
    api
      .getYardiCustomRecordsByBuildingId(selectedBuilding.id)
      .then((response) => {
        setYardiCustomRecords(response.data)
      })
      // Fetched for every building; Yardi sync is optional, so a failure leaves it null
      .catch(() => {})
  }, [])

  return (
    <ModalContainer
      additionalClass="checklist-modal"
      onClose={onClose}
      headerTitle={
        isIntegrationTask
          ? t('checklists-create-task:updateCustomTaskModal.editIntegrationHeaderTitle')
          : t('checklists-create-task:customTaskForm.editHeaderTitle')
      }
      headerIcon={<Icon icon="checkClipboard" />}
    >
      <div className={`${isLoading ? 'is-loading' : ''}`}>
        {isLoading ? (
          <div className="checklist-modal-content checklist-task-data" />
        ) : (
          <React.Fragment>
            {!isIntegrationTask && (
              <ul className="edit-task-tabs">
                <li
                  className={currentTab === TABS.TASK_DATA ? 'is-active' : ''}
                  onClick={() => onChangeTab(TABS.TASK_DATA)}
                >
                  <span>{t('checklists-create-task:taskSteps.taskData')}</span>
                </li>
                <li
                  className={currentTab === TABS.TASK_SETTINGS ? 'is-active' : ''}
                  onClick={() => onChangeTab(TABS.TASK_SETTINGS)}
                >
                  <span>{t('checklists-create-task:taskSteps.taskSettings')}</span>
                </li>
              </ul>
            )}
            {currentTab === TABS.TASK_DATA && !isIntegrationTask ? (
              <TaskData
                handleFieldChange={handleFieldChange}
                onBack={onClose}
                onHandleNext={onSave}
                formState={formState}
                nameError={nameError}
                setNameError={setNameError}
                noInputsError={noInputsError}
                selectedBuilding={selectedBuilding}
                yardiCustomRecords={yardiCustomRecords}
                setYardiCustomRecords={setYardiCustomRecords}
                isEdit
              />
            ) : null}
            {currentTab === TABS.TASK_SETTINGS ? (
              <TaskSettings
                handleFieldChange={handleFieldChange}
                handleBack={onClose}
                onSubmit={onSave}
                formState={formState}
                availableStatuses={availableStatuses}
                isIntegrationTask={isIntegrationTask}
                nameError={nameError}
                isEdit
                getAmenities={getAmenities}
                amenities={amenities}
                buildingId={selectedBuilding.id}
                isAdmin={isAdmin}
              />
            ) : null}
          </React.Fragment>
        )}
      </div>
    </ModalContainer>
  )
}

ChecklistsUpdateCustomTaskModal.propTypes = {
  selectedBuilding: PropTypes.object,
  taskId: PropTypes.number,
  onClose: PropTypes.func,
  fetchSingleBuilding: PropTypes.func,
  getAmenities: PropTypes.func,
  amenities: PropTypes.array,
  userRole: PropTypes.string,
}

export default connect(
  (state) => ({
    amenities: selectAmenities(state),
    userRole: selectUserRole(state),
  }),
  { fetchSingleBuilding, getAmenities },
)(ChecklistsUpdateCustomTaskModal)
